import network from "network/network";
import { chatTrace } from "utils/chatTrace";
import actionTypes from "./actionTypes";

// Race condition'ı önlemek için request tracking
let currentRequestId = 0;

const PAGE_SIZE = 10; // Her sayfada gösterilecek müşteri sayısı

const DEFAULT_CHAT_LIST_QUERY = Object.freeze({
    searchText: "",
    activityId: null
});

const normalizeChatListQuery = (query = {}) => {
    const searchText = typeof query?.searchText === "string" ? query.searchText.trim() : "";
    const rawActivityId = query?.activityId;
    const parsedActivityId = rawActivityId === null || rawActivityId === undefined || rawActivityId === "" || rawActivityId === "all"
        ? null
        : Number(rawActivityId);

    return {
        searchText,
        activityId: parsedActivityId !== null && !Number.isNaN(parsedActivityId) ? parsedActivityId : null
    };
};

const resolveChatListQuery = (requestedQuery, storedQuery) => {
    const hasExplicitQuery = requestedQuery
        && (Object.prototype.hasOwnProperty.call(requestedQuery, "searchText")
            || Object.prototype.hasOwnProperty.call(requestedQuery, "activityId"));

    return normalizeChatListQuery(hasExplicitQuery ? requestedQuery : (storedQuery || DEFAULT_CHAT_LIST_QUERY));
};

const normalizeBoolean = (value) => {
    if (value === true || value === false) {
        return value;
    }

    if (value === 1 || value === 0) {
        return Boolean(value);
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true" || normalized === "1") {
            return true;
        }
        if (normalized === "false" || normalized === "0") {
            return false;
        }
    }

    return undefined;
};

const resolveBoolean = (...candidates) => {
    for (const candidate of candidates) {
        const normalized = normalizeBoolean(candidate);
        if (normalized !== undefined) {
            return normalized;
        }
    }

    return undefined;
};

const resolveStatusFlags = (source = {}) => {
    const rawIsAdminTransaction = resolveBoolean(
        source?.isAdminTransaction,
        source?.IsAdminTransaction,
        source?.isadmintransaction,
        source?.ISADMINTRANSACTION,
        source?.IS_ADMIN_TRANSACTION
    );
    const rawIsOpenAdmin = resolveBoolean(
        source?.isOpenAdmin,
        source?.IsOpenAdmin,
        source?.isopenadmin,
        source?.ISOPENADMIN,
        source?.IS_OPEN_ADMIN
    );
    const rawIsOpenTransaction = resolveBoolean(
        source?.isOpenTransaction,
        source?.IsOpenTransaction,
        source?.isopentransaction,
        source?.ISOPENTRANSACTION,
        source?.IS_OPEN_TRANSACTION
    );

    const isAdminTransaction = rawIsAdminTransaction ?? rawIsOpenAdmin;
    const isOpenAdmin = rawIsOpenAdmin ?? rawIsAdminTransaction;
    const isOpenTransaction = rawIsOpenTransaction ?? isAdminTransaction ?? isOpenAdmin;

    return {
        isAdminTransaction,
        isOpenTransaction,
        isOpenAdmin,
        rawIsAdminTransaction,
        rawIsOpenTransaction,
        rawIsOpenAdmin
    };
};

const MESSAGE_TYPE_LABELS = ["Satış İçerikli", "Satış Dışı", "Niteliksiz"];

const normalizeTextKey = (value) => String(value)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/[^a-z0-9]/g, "");

const normalizeMessageType = (value) => {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    if (typeof value === "number") {
        return MESSAGE_TYPE_LABELS[value];
    }

    const trimmedValue = String(value).trim();
    const numericValue = Number(trimmedValue);
    if (trimmedValue !== "" && !Number.isNaN(numericValue)) {
        return MESSAGE_TYPE_LABELS[numericValue];
    }

    const normalizedValue = normalizeTextKey(trimmedValue);
    const matchingLabel = MESSAGE_TYPE_LABELS.find(label => normalizeTextKey(label) === normalizedValue);

    return matchingLabel || trimmedValue;
};

const getMessageTypeDebugCandidates = (source = {}) => ({
    messageType: source?.messageType,
    MessageType: source?.MessageType,
    MESSAGETYPE: source?.MESSAGETYPE,
    messagetype: source?.messagetype,
    message_type: source?.message_type,
    MESSAGE_TYPE: source?.MESSAGE_TYPE,
    messageTypeName: source?.messageTypeName,
    MessageTypeName: source?.MessageTypeName,
    MESSAGETYPENAME: source?.MESSAGETYPENAME,
    messagetypename: source?.messagetypename,
    messageTypeDesc: source?.messageTypeDesc,
    MessageTypeDesc: source?.MessageTypeDesc,
    MESSAGETYPEDESC: source?.MESSAGETYPEDESC,
    messagetypedesc: source?.messagetypedesc,
    messageTypeDescription: source?.messageTypeDescription,
    MessageTypeDescription: source?.MessageTypeDescription,
    messageTypeId: source?.messageTypeId,
    MessageTypeId: source?.MessageTypeId,
    MESSAGETYPEID: source?.MESSAGETYPEID,
    messagetypeid: source?.messagetypeid
});

const resolveMessageType = (source = {}) => {
    const candidates = getMessageTypeDebugCandidates(source);
    const firstCandidate = Object.values(candidates).find(value => value !== null && value !== undefined && value !== "");

    return normalizeMessageType(firstCandidate);
};

function buildApiAdminAuditEntries(messages = []) {
    if (!Array.isArray(messages)) {
        return [];
    }

    return messages.map((item, index) => ({
        index,
        customerNumber: item?.customerNumber || item?.sourcePhone || item?.source || item?.destination || "",
        customerName: item?.customerName || "",
        customerStatus: item?.customerStatus,
        apicustomerStatus: item?.customerStatus,
        isAdminTransaction: item?.isAdminTransaction,
        isOpenTransaction: item?.isOpenTransaction,
        isOpenAdmin: item?.isOpenAdmin,
        activityId: item?.activityId,
        activityName: item?.activityName,
        sendType: item?.sendType,
        apiAuditRequestId: item?.__apiAuditRequestId,
        apiAuditPage: item?.__apiAuditPage,
        apiAuditIndex: item?.__apiAuditIndex
    }));
}

export function selectChatList(user, showLoading = true, page = 1) {

    return (dispatch, getState) => {
        const query = resolveChatListQuery(user, getState().chatReducer.query);

        // Bu isteğin ID'sini kaydet ve istek durumunu güncelle
        const requestId = ++currentRequestId;

        // İlk sayfa için loading göster, sonraki sayfalar için loadingMore kullan
        if (page === 1) {
            // Store the requested filters synchronously. SignalR and other refresh
            // calls use this value, so they cannot replace an in-flight filtered
            // request with the previous unfiltered query.
            dispatch({
                type: actionTypes.SELECT_CHAT_LIST_QUERY_CHANGED,
                query
            });

            if (showLoading) {
                dispatch({
                    type: actionTypes.SELECT_CHAT_LIST_LOADING,
                    isLoading: true
                })
            }
        } else {
            dispatch({
                type: actionTypes.SELECT_CHAT_LIST_HAS_MORE,
                isLoadingMore: true
            })
        }

        const params = {
            page: page,
            pageSize: PAGE_SIZE
        };

        if (query.searchText) {
            params.searchText = query.searchText;
        }
        if (query.activityId !== null) {
            params.activityId = query.activityId;
        }

        chatTrace("chatList.request.started", {
            requestId,
            page,
            showLoading,
            pageSize: PAGE_SIZE,
            query
        });

        network.post("/Messages/searchFirmMessageMaster", params).then((result) => {
            
            // Eğer bu eski bir istek ise (yeni istek atıldıysa), sonucu yoksay
            if (requestId !== currentRequestId) {
                chatTrace("chatList.request.ignored", {
                    requestId,
                    currentRequestId,
                    page,
                    reason: "stale_response"
                }, "warn");
                return;
            }

            // İstek tamamlandı
            if (page === 1 && showLoading) {
                dispatch({
                    type: actionTypes.SELECT_CHAT_LIST_LOADING,
                    isLoading: false
                })
            }

            const { data } = result;
            
            if (result.isSuccess && data.isSuccess) {
                // API yanıtı: { data: { items: [...], totalCount, page, pageSize, totalPages, hasMore } }
                var responseData = data.data;
                var messages = responseData.items || responseData;

                if (Array.isArray(messages)) {
                    messages = messages.map((item, index) => {
                        const {
                            isAdminTransaction,
                            isOpenTransaction,
                            isOpenAdmin,
                            rawIsAdminTransaction,
                            rawIsOpenTransaction,
                            rawIsOpenAdmin
                        } = resolveStatusFlags(item);
                        const rawMessageTypeCandidates = getMessageTypeDebugCandidates(item);
                        const resolvedMessageType = resolveMessageType(item);

                        return {
                            ...item,
                            customerStatus: item?.customerStatus ?? item?.customerStatus ?? item?.customerStatus ?? item?.customerStatus ?? item?.CLIENT_STATUS,
                            messageType: resolvedMessageType,
                            isAdminTransaction,
                            isOpenTransaction,
                            isOpenAdmin,
                            __apiIsAdminTransaction: rawIsAdminTransaction,
                            __apiIsOpenTransaction: rawIsOpenTransaction,
                            __apiIsOpenAdmin: rawIsOpenAdmin,
                            __apiActivityId: item?.activityId,
                            __apiAuditRequestId: requestId,
                            __apiAuditPage: page,
                            __apiAuditIndex: index
                        };
                    });
                }

                const apiAdminAuditEntries = buildApiAdminAuditEntries(messages);
                
                // Backend'den gelen hasMore bilgisi veya hesapla
                var hasMore = responseData.hasNextPage !== undefined
                    ? responseData.hasNextPage
                    : (responseData.hasMore !== undefined
                        ? responseData.hasMore
                        : (responseData.page < responseData.totalPages));

                chatTrace("chatList.request.succeeded", {
                    requestId,
                    page,
                    itemCount: Array.isArray(messages) ? messages.length : 0,
                    hasMore,
                    apiPage: responseData.page,
                    totalPages: responseData.totalPages
                });
                
                if (page === 1) {
                    dispatch({
                        type: actionTypes.SELECT_CHAT_LIST_SUCCESS,
                        payload: messages,
                        hasMore: hasMore,
                        query,
                        adminAuditRequestId: requestId,
                        adminAuditPage: page
                    })
                } else {
                    dispatch({
                        type: actionTypes.SELECT_CHAT_LIST_APPEND,
                        payload: messages,
                        page: page,
                        hasMore: hasMore,
                        query,
                        adminAuditRequestId: requestId,
                        adminAuditPage: page
                    })
                }
            } else {
                chatTrace("chatList.request.failed", {
                    requestId,
                    page,
                    errorMessage: result.errorMessage
                }, "error");
                dispatch({
                    type: actionTypes.SELECT_CHAT_LIST_ERROR,
                    errorMessage: result.errorMessage,
                    hasError: true
                })
                if (page > 1) {
                    dispatch({
                        type: actionTypes.SELECT_CHAT_LIST_HAS_MORE,
                        isLoadingMore: false
                    })
                }
            }

        }).catch((error) => {
            if (requestId !== currentRequestId) {
                return;
            }

            if (page === 1 && showLoading) {
                dispatch({
                    type: actionTypes.SELECT_CHAT_LIST_LOADING,
                    isLoading: false
                });
            } else if (page > 1) {
                dispatch({
                    type: actionTypes.SELECT_CHAT_LIST_HAS_MORE,
                    isLoadingMore: false
                });
            }

            chatTrace("chatList.request.exception", {
                requestId,
                page,
                error: error?.message || error
            }, "error");
        });
    }
};

export function loadMoreChatList() {
    return (dispatch, getState) => {
        const { currentPage, hasMore, isLoading, isLoadingMore, query } = getState().chatReducer;
        
        if (!hasMore || isLoading || isLoadingMore) {
            chatTrace("chatList.loadMore.skipped", {
                currentPage,
                hasMore,
                isLoading,
                isLoadingMore
            });
            return;
        }

        chatTrace("chatList.loadMore.started", {
            currentPage,
            nextPage: currentPage + 1
        });
        
        dispatch(selectChatList(query, false, currentPage + 1));
    }
};



export function resetUnreadCount(number) {

    return (dispatch, getState) => {


        dispatch({
            type: actionTypes.RESET_UNREAD_COUNT,
            customerNumber: number
        })
    }
};

export function selectChatDetailList(user, showLoading = true) {

    return (dispatch, getState) => {

        chatTrace("chatDetail.request.started", {
            customerNumber: user?.customerNumber,
            showLoading
        });

        if (showLoading) {
            dispatch({
                type: actionTypes.SELECT_CHAT_DETAIL_LIST_LOADING,
                isLoading: true
            })
        }

        network.get("/messages/selectMessageDetail", user, false).then((result) => {

            if (showLoading) {
                dispatch({
                    type: actionTypes.SELECT_CHAT_DETAIL_LIST_LOADING,
                    isLoading: false
                })
            }
            const { data } = result;
            if (result.isSuccess && data.isSuccess) {

                var messageMaster = (data.data);
                const normalizeProductText = (value) => {
                    if (value === null || value === undefined) {
                        return "";
                    }

                    const text = String(value).trim();
                    if (!text) {
                        return "";
                    }

                    const normalized = text.toLowerCase();
                    const placeholderValues = [
                        "urun bilgisi yok",
                        "ürün bilgisi yok",
                        "product info not found",
                        "urun",
                        "ürün",
                        "product",
                        "yok",
                        "-",
                        "n/a",
                        "null",
                        "undefined"
                    ];

                    return placeholderValues.includes(normalized) ? "" : text;
                };

                const findProductLikeValue = (source, depth = 0) => {
                    if (!source || typeof source !== "object" || depth > 2) {
                        return "";
                    }

                    for (const [key, value] of Object.entries(source)) {
                        if (value === null || value === undefined) {
                            continue;
                        }

                        if (typeof value === "string" && /product|urun/i.test(key)) {
                            const normalized = normalizeProductText(value);
                            if (normalized) {
                                return normalized;
                            }
                        }

                        if (typeof value === "object") {
                            const nested = findProductLikeValue(value, depth + 1);
                            if (nested) {
                                return nested;
                            }
                        }
                    }

                    return "";
                };

                const resolvedCustomerNumber =
                    messageMaster.customerNumber ||
                    messageMaster.customernumber ||
                    messageMaster.CUSTOMERNUMBER ||
                    user?.customerNumber ||
                    "";

                const resolvedProductName =
                    normalizeProductText(messageMaster.productName) ||
                    normalizeProductText(messageMaster.productname) ||
                    normalizeProductText(messageMaster.PRODUCTNAME) ||
                    normalizeProductText(messageMaster.product) ||
                    normalizeProductText(messageMaster.productDesc) ||
                    normalizeProductText(messageMaster.productdesc) ||
                    normalizeProductText(messageMaster.PRODUCTDESC) ||
                    findProductLikeValue(messageMaster) ||
                    "";

                const {
                    isAdminTransaction: resolvedIsAdminTransaction,
                    isOpenTransaction: resolvedIsOpenTransaction,
                    isOpenAdmin: resolvedIsOpenAdmin
                } = resolveStatusFlags(messageMaster);
                const rawMessageTypeCandidates = getMessageTypeDebugCandidates(messageMaster);
                const resolvedMessageType = resolveMessageType(messageMaster);

                dispatch({
                    type: actionTypes.SELECT_CHAT_DETAIL_LIST_SUCCESS,
                    payload: messageMaster.messageList,
                    customerName: messageMaster.customerName,
                    customerId: messageMaster.customerId,
                    customerImage: messageMaster.customerImage,
                    customerNumber: resolvedCustomerNumber,
                    isAdminTransaction: resolvedIsAdminTransaction,
                    productName: resolvedProductName,
                    activityName:messageMaster.activityName,
                    activityId:messageMaster.activityId,
                    messageType: resolvedMessageType,
                    isOpenTransaction: resolvedIsOpenTransaction,
                    isOpenAdmin: resolvedIsOpenAdmin
                })

                chatTrace("chatDetail.request.succeeded", {
                    customerNumber: resolvedCustomerNumber,
                    customerId: messageMaster.customerId,
                    messageCount: Array.isArray(messageMaster.messageList) ? messageMaster.messageList.length : 0,
                    isAdminTransaction: resolvedIsAdminTransaction,
                    isOpenTransaction: resolvedIsOpenTransaction,
                    isOpenAdmin: resolvedIsOpenAdmin,
                    activityId: messageMaster.activityId,
                    activityName: messageMaster.activityName,
                    messageType: resolvedMessageType
                });


            } else {
                chatTrace("chatDetail.request.failed", {
                    customerNumber: user?.customerNumber,
                    errorMessage: result.errorMessage
                }, "error");
                dispatch({
                    type: actionTypes.SELECT_CHAT_DETAIL_LIST_ERROR,
                    errorMessage: result.errorMessage,
                    hasError: true
                })
            }

        }).catch((error) => {
            chatTrace("chatDetail.request.exception", {
                customerNumber: user?.customerNumber,
                error: error?.message || error
            }, "error");
        });
    }
};



export function finishAdminChat(params) {

    return (dispatch, getState) => {
        return network.get("/messages/finishAdminChat", params, false).then((result) => {
            if (result.isSuccess) {
                dispatch({
                    type: actionTypes.FINISH_ADMIN_CHAT,
                });
                return { success: true };
            }

            return {
                success: false,
                error: result.errorMessage || "Sohbet tamamlanamadı"
            };
        });
    }
};

export function startAdminChat() {

    return (dispatch, getState) => {

        dispatch({
            type: actionTypes.START_ADMIN_CHAT,
        })
    }
}

export function startManualChat(params) {
    return (dispatch, getState) => {
        chatTrace("chat.manual.start.requested", {
            customerNumber: params?.customerNumber,
            customerId: params?.customerId
        });

        dispatch({
            type: actionTypes.START_MANUAL_CHAT_LOADING,
            isLoading: true
        })

        return network.post("/Messages/startAdminChat", params, false).then((result) => {
            dispatch({
                type: actionTypes.START_MANUAL_CHAT_LOADING,
                isLoading: false
            })

            const { data } = result;
            if (result.isSuccess && data.isSuccess) {
                dispatch({
                    type: actionTypes.START_MANUAL_CHAT_SUCCESS,
                    customerNumber: params.customerNumber
                })

                chatTrace("chat.manual.start.succeeded", {
                    customerNumber: params.customerNumber,
                    customerId: params.customerId
                });
                
                // Refresh chat list to show updated status
                dispatch(selectChatList({}));
                
                return { success: true };
            } else {
                chatTrace("chat.manual.start.failed", {
                    customerNumber: params.customerNumber,
                    customerId: params.customerId,
                    errorMessage: result.errorMessage || data.errorMessage
                }, "error");
                dispatch({
                    type: actionTypes.START_MANUAL_CHAT_ERROR,
                    errorMessage: result.errorMessage || data.errorMessage,
                    hasError: true
                })
                return { success: false, error: result.errorMessage || data.errorMessage };
            }

        }).catch((error) => {
            chatTrace("chat.manual.start.exception", {
                customerNumber: params?.customerNumber,
                customerId: params?.customerId,
                error: error?.message || error
            }, "error");

            dispatch({
                type: actionTypes.START_MANUAL_CHAT_LOADING,
                isLoading: false
            })
            dispatch({
                type: actionTypes.START_MANUAL_CHAT_ERROR,
                errorMessage: "Manuel sohbet başlatılırken hata oluştu",
                hasError: true
            })
            return { success: false, error: "Manuel sohbet başlatılırken hata oluştu" };
        });
    }
};

export function selectChatFilterOptions() {

    return (dispatch, getState) => {

        dispatch({
            type: actionTypes.SELECT_CHAT_FILTER_OPTIONS_LOADING,
            isLoading: true
        })

        network.get("/admin/Activity/getAllActivities2", null, false).then((result) => {
            dispatch({
                type: actionTypes.SELECT_CHAT_FILTER_OPTIONS_LOADING,
                isLoading: false
            })

            const { data } = result;

            if (result.isSuccess && data.isSuccess) {
                var filterOptions = data.data;
                dispatch({
                    type: actionTypes.SELECT_CHAT_FILTER_OPTIONS_SUCCESS,
                    payload: filterOptions
                })
            } else {
                dispatch({
                    type: actionTypes.SELECT_CHAT_FILTER_OPTIONS_ERROR,
                    errorMessage: result.errorMessage,
                    hasError: true
                })
            }

        }).catch((error) => {
            dispatch({
                type: actionTypes.SELECT_CHAT_FILTER_OPTIONS_LOADING,
                isLoading: false
            })
            dispatch({
                type: actionTypes.SELECT_CHAT_FILTER_OPTIONS_ERROR,
                errorMessage: "Filter seçenekleri yüklenirken hata oluştu",
                hasError: true
            })
        });
    }
};

export function changeConversationActivityStatus(params, skipChatListRefresh = false) {

    return (dispatch, getState) => {

        chatTrace("chat.activity.change.requested", {
            customerNumber: params?.customerNumber,
            customerId: params?.customerId,
            activityId: params?.activityId,
            activityName: params?.activityName,
            skipChatListRefresh
        });

        dispatch({
            type: actionTypes.CHANGE_CONVERSATION_ACTIVITY_STATUS_LOADING,
            isLoading: true
        })

        return network.post("/Messages/changeCustomerActivityStatus", params, false).then((result) => {
            dispatch({
                type: actionTypes.CHANGE_CONVERSATION_ACTIVITY_STATUS_LOADING,
                isLoading: false
            })

            const { data } = result;
            if (result.isSuccess && data.isSuccess) {
                dispatch({
                    type: actionTypes.CHANGE_CONVERSATION_ACTIVITY_STATUS_SUCCESS,
                    customerNumber: params.customerNumber,
                    activityId: params.activityId,
                    activityName: params.activityName
                })

                chatTrace("chat.activity.change.succeeded", {
                    customerNumber: params.customerNumber,
                    customerId: params.customerId,
                    activityId: params.activityId,
                    activityName: params.activityName,
                    skipChatListRefresh
                });
                
                // Only refresh chat list if skipChatListRefresh is false
                if (!skipChatListRefresh) {
                    dispatch(selectChatList({}));
                }
                
                return { success: true };
            } else {
                chatTrace("chat.activity.change.failed", {
                    customerNumber: params?.customerNumber,
                    customerId: params?.customerId,
                    activityId: params?.activityId,
                    errorMessage: result.errorMessage || data.errorMessage
                }, "error");
                dispatch({
                    type: actionTypes.CHANGE_CONVERSATION_ACTIVITY_STATUS_ERROR,
                    errorMessage: result.errorMessage || data.errorMessage,
                    hasError: true
                })
                return { success: false, error: result.errorMessage || data.errorMessage };
            }

        }).catch((error) => {
            chatTrace("chat.activity.change.exception", {
                customerNumber: params?.customerNumber,
                customerId: params?.customerId,
                activityId: params?.activityId,
                error: error?.message || error
            }, "error");

            dispatch({
                type: actionTypes.CHANGE_CONVERSATION_ACTIVITY_STATUS_LOADING,
                isLoading: false
            })
            dispatch({
                type: actionTypes.CHANGE_CONVERSATION_ACTIVITY_STATUS_ERROR,
                errorMessage: "Aktivite durumu değiştirilirken hata oluştu",
                hasError: true
            })
            return { success: false, error: "Aktivite durumu değiştirilirken hata oluştu" };
        });
    }
};

export function changeConversationMessageType(params, skipChatListRefresh = false) {

    return (dispatch, getState) => {

        chatTrace("chat.messageType.change.requested", {
            customerNumber: params?.customerNumber,
            customerId: params?.customerId,
            messageType: params?.messageType,
            skipChatListRefresh
        });

        dispatch({
            type: actionTypes.CHANGE_CONVERSATION_MESSAGE_TYPE_LOADING,
            isLoading: true
        })

        return network.post("/Messages/setMessageType", params, false).then((result) => {
            dispatch({
                type: actionTypes.CHANGE_CONVERSATION_MESSAGE_TYPE_LOADING,
                isLoading: false
            })

            const { data } = result;

            if (result.isSuccess && data.isSuccess) {
                dispatch({
                    type: actionTypes.CHANGE_CONVERSATION_MESSAGE_TYPE_SUCCESS,
                    customerNumber: params.customerNumber,
                    messageType: params.messageType
                })

                chatTrace("chat.messageType.change.succeeded", {
                    customerNumber: params.customerNumber,
                    customerId: params.customerId,
                    messageType: params.messageType,
                    skipChatListRefresh
                });

                if (!skipChatListRefresh) {
                    dispatch(selectChatList({}));
                }

                return { success: true };
            } else {
                chatTrace("chat.messageType.change.failed", {
                    customerNumber: params?.customerNumber,
                    customerId: params?.customerId,
                    messageType: params?.messageType,
                    errorMessage: result.errorMessage || data.errorMessage
                }, "error");
                dispatch({
                    type: actionTypes.CHANGE_CONVERSATION_MESSAGE_TYPE_ERROR,
                    errorMessage: result.errorMessage || data.errorMessage,
                    hasError: true
                })
                return { success: false, error: result.errorMessage || data.errorMessage };
            }

        }).catch((error) => {
            chatTrace("chat.messageType.change.exception", {
                customerNumber: params?.customerNumber,
                customerId: params?.customerId,
                messageType: params?.messageType,
                error: error?.message || error
            }, "error");

            dispatch({
                type: actionTypes.CHANGE_CONVERSATION_MESSAGE_TYPE_LOADING,
                isLoading: false
            })
            dispatch({
                type: actionTypes.CHANGE_CONVERSATION_MESSAGE_TYPE_ERROR,
                errorMessage: "Sohbet tipi değiştirilirken hata oluştu",
                hasError: true
            })
            return { success: false, error: "Sohbet tipi değiştirilirken hata oluştu" };
        });
    }
};

export function updateChatItemStatus(customerNumber) {
    return (dispatch, getState) => {
        // HİÇBİR ŞEY YAPMA! Loading olmasın diye
        // API çağrısı YOK, loading YOK!
    }
};
